import React, { useState, useEffect, useMemo } from 'react';
import api from '../api';
import { Layers, Plus, Edit2, Trash2, Search, CheckCircle, XCircle, AlertTriangle, X, ChevronRight } from 'lucide-react';
import './CategoryManagement.css';

const CategoryManagement = () => {
    // Data lists
    const [industries, setIndustries] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);

    // Selections
    const [selectedIndustry, setSelectedIndustry] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);

    // Loading states
    const [loadingIndustries, setLoadingIndustries] = useState(true);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [loadingSubcategories, setLoadingSubcategories] = useState(false);

    // Search queries
    const [searchIndustry, setSearchIndustry] = useState('');
    const [searchCategory, setSearchCategory] = useState('');
    const [searchSubcategory, setSearchSubcategory] = useState('');

    // Modal state for Add/Edit
    const [modalState, setModalState] = useState({
        isOpen: false,
        mode: 'ADD', // 'ADD' | 'EDIT'
        level: 'INDUSTRY', // 'INDUSTRY' | 'CATEGORY' | 'SUBCATEGORY' | 'CORE'
        item: null,
        parent: null
    });
    const [formData, setFormData] = useState({ name: '', status: 'ACTIVE' });
    const [formSubmitting, setFormSubmitting] = useState(false);

    // Delete Confirmation Modal
    const [deleteModal, setDeleteModal] = useState({
        isOpen: false,
        level: '',
        item: null,
        loading: false,
        error: null
    });

    // Toast
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 4000);
    };

    // 1. Fetch Industries on load
    useEffect(() => {
        fetchIndustries();
    }, []);

    const fetchIndustries = async () => {
        setLoadingIndustries(true);
        try {
            const res = await api.get('/expertise-categories');
            setIndustries(res.data || []);
        } catch (err) {
            console.error("Failed to fetch industries:", err);
            showToast("Failed to load Industries", "error");
        } finally {
            setLoadingIndustries(false);
        }
    };

    // 2. Fetch Categories when Industry changes
    const fetchCategories = async (industryId) => {
        if (!industryId) {
            setCategories([]);
            return;
        }
        setLoadingCategories(true);
        try {
            const res = await api.get(`/expertise-subcategories?categoryId=${industryId}`);
            setCategories(res.data || []);
        } catch (err) {
            console.error("Failed to fetch categories:", err);
            showToast("Failed to load Expertise Categories", "error");
        } finally {
            setLoadingCategories(false);
        }
    };

    // 3. Fetch Subcategories when Category changes
    const fetchSubcategories = async (categoryId) => {
        if (!categoryId) {
            setSubcategories([]);
            return;
        }
        setLoadingSubcategories(true);
        try {
            const res = await api.get(`/core-expertise?subcategoryId=${categoryId}`);
            setSubcategories(res.data || []);
        } catch (err) {
            console.error("Failed to fetch subcategories:", err);
            showToast("Failed to load Sub Categories", "error");
        } finally {
            setLoadingSubcategories(false);
        }
    };

    // Handle Selections
    const handleSelectIndustry = (ind) => {
        setSelectedIndustry(ind);
        setSelectedCategory(null);
        setSelectedSubcategory(null);
        setSubcategories([]);
        fetchCategories(ind.id);
    };

    const handleSelectCategory = (cat) => {
        setSelectedCategory(cat);
        setSelectedSubcategory(null);
        fetchSubcategories(cat.id);
    };

    const handleSelectSubcategory = (sub) => {
        setSelectedSubcategory(sub);
    };

    // Filtered lists for search
    const filteredIndustries = useMemo(() => {
        return industries.filter(i => i.name.toLowerCase().includes(searchIndustry.toLowerCase()));
    }, [industries, searchIndustry]);

    const filteredCategories = useMemo(() => {
        return categories.filter(c => c.name.toLowerCase().includes(searchCategory.toLowerCase()));
    }, [categories, searchCategory]);

    const filteredSubcategories = useMemo(() => {
        return subcategories.filter(s => s.name.toLowerCase().includes(searchSubcategory.toLowerCase()));
    }, [subcategories, searchSubcategory]);

    // Open Modal
    const openAddModal = (level, parent) => {
        setFormData({ name: '', status: 'ACTIVE' });
        setModalState({ isOpen: true, mode: 'ADD', level, item: null, parent });
    };

    const openEditModal = (level, item, parent) => {
        setFormData({ name: item.name, status: item.status || 'ACTIVE' });
        setModalState({ isOpen: true, mode: 'EDIT', level, item, parent });
    };

    const closeModal = () => {
        setModalState({ isOpen: false, mode: 'ADD', level: 'INDUSTRY', item: null, parent: null });
    };

    // Submit Add/Edit
    const handleSubmitModal = async (e) => {
        e.preventDefault();
        const cleanName = formData.name.trim();
        if (!cleanName) {
            showToast("Name cannot be empty", "error");
            return;
        }

        setFormSubmitting(true);
        try {
            const { mode, level, item, parent } = modalState;
            let res;

            if (mode === 'ADD') {
                if (level === 'INDUSTRY') {
                    res = await api.post('/expertise-categories', { name: cleanName, status: formData.status });
                    setIndustries(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
                    handleSelectIndustry(res.data);
                } else if (level === 'CATEGORY') {
                    res = await api.post('/expertise-subcategories', { expertise_category_id: parent.id, name: cleanName, status: formData.status });
                    setCategories(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
                    handleSelectCategory(res.data);
                } else if (level === 'SUBCATEGORY') {
                    res = await api.post('/core-expertise', { subcategory_id: parent.id, name: cleanName, status: formData.status });
                    setSubcategories(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
                    handleSelectSubcategory(res.data);
                }
                showToast(`${getLevelTitle(level)} added successfully`);
            } else {
                // EDIT
                let url = '';
                if (level === 'INDUSTRY') url = `/expertise-categories/${item.id}`;
                else if (level === 'CATEGORY') url = `/expertise-subcategories/${item.id}`;
                else if (level === 'SUBCATEGORY') url = `/core-expertise/${item.id}`;

                res = await api.put(url, { name: cleanName, status: formData.status });
                const updated = res.data;

                if (level === 'INDUSTRY') {
                    setIndustries(prev => prev.map(i => i.id === updated.id ? updated : i).sort((a, b) => a.name.localeCompare(b.name)));
                    if (selectedIndustry?.id === updated.id) setSelectedIndustry(updated);
                } else if (level === 'CATEGORY') {
                    setCategories(prev => prev.map(c => c.id === updated.id ? updated : c).sort((a, b) => a.name.localeCompare(b.name)));
                    if (selectedCategory?.id === updated.id) setSelectedCategory(updated);
                } else if (level === 'SUBCATEGORY') {
                    setSubcategories(prev => prev.map(s => s.id === updated.id ? updated : s).sort((a, b) => a.name.localeCompare(b.name)));
                    if (selectedSubcategory?.id === updated.id) setSelectedSubcategory(updated);
                }
                showToast(`${getLevelTitle(level)} updated successfully`);
            }
            closeModal();
        } catch (err) {
            const msg = err.response?.data?.detail || err.message || "Operation failed";
            showToast(msg, "error");
        } finally {
            setFormSubmitting(false);
        }
    };

    // Open Delete Modal
    const openDeleteConfirm = (e, level, item) => {
        e.stopPropagation();
        setDeleteModal({ isOpen: true, level, item, loading: false, error: null });
    };

    const closeDeleteConfirm = () => {
        setDeleteModal({ isOpen: false, level: '', item: null, loading: false, error: null });
    };

    const handleConfirmDelete = async () => {
        const { level, item } = deleteModal;
        setDeleteModal(prev => ({ ...prev, loading: true, error: null }));
        try {
            let url = '';
            if (level === 'INDUSTRY') url = `/expertise-categories/${item.id}`;
            else if (level === 'CATEGORY') url = `/expertise-subcategories/${item.id}`;
            else if (level === 'SUBCATEGORY') url = `/core-expertise/${item.id}`;

            await api.delete(url);

            if (level === 'INDUSTRY') {
                setIndustries(prev => prev.filter(i => i.id !== item.id));
                if (selectedIndustry?.id === item.id) {
                    setSelectedIndustry(null);
                    setCategories([]);
                    setSubcategories([]);
                }
            } else if (level === 'CATEGORY') {
                setCategories(prev => prev.filter(c => c.id !== item.id));
                if (selectedCategory?.id === item.id) {
                    setSelectedCategory(null);
                    setSubcategories([]);
                }
            } else if (level === 'SUBCATEGORY') {
                setSubcategories(prev => prev.filter(s => s.id !== item.id));
                if (selectedSubcategory?.id === item.id) {
                    setSelectedSubcategory(null);
                }
            }

            showToast(`${getLevelTitle(level)} deleted successfully`);
            closeDeleteConfirm();
        } catch (err) {
            const msg = err.response?.data?.detail || err.message || "Failed to delete item";
            setDeleteModal(prev => ({ ...prev, loading: false, error: msg }));
        }
    };

    const getLevelTitle = (level) => {
        if (level === 'INDUSTRY') return 'Category';
        if (level === 'CATEGORY') return 'Sub Category';
        if (level === 'SUBCATEGORY') return 'Core Expertise';
        return 'Item';
    };

    return (
        <div className="cat-mgmt-container">
            {/* Toast Notification */}
            {toast.show && (
                <div style={{
                    position: 'fixed',
                    top: '20px',
                    right: '20px',
                    padding: '12px 20px',
                    borderRadius: '8px',
                    backgroundColor: toast.type === 'error' ? '#ef4444' : '#10b981',
                    color: '#ffffff',
                    fontWeight: 600,
                    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
                    zIndex: 2000,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    {toast.type === 'error' ? <AlertTriangle size={18} /> : <CheckCircle size={18} />}
                    {toast.message}
                </div>
            )}

            <div className="cat-mgmt-header">
                <h2><Layers size={24} color="#0284c7" /> Dynamic Category Hierarchy</h2>
                <p>Manage and configure your institution's 3-level taxonomy. Changes reflect across POP Expert onboarding dynamically.</p>
            </div>

            <div className="cat-columns-grid">
                {/* COLUMN 1: INDUSTRIES */}
                <div className="cat-col-card">
                    <div className="cat-col-header">
                        <div className="cat-col-title-row">
                            <span className="cat-col-title">
                                Categories
                                <span className="cat-col-badge">{industries.length}</span>
                            </span>
                            <button className="cat-add-btn" onClick={() => openAddModal('INDUSTRY', null)}>
                                <Plus size={14} /> Add Category
                            </button>
                        </div>
                        <div className="cat-col-search">
                            <Search size={14} className="cat-search-icon" />
                            <input
                                type="text"
                                placeholder="Search category..."
                                value={searchIndustry}
                                onChange={e => setSearchIndustry(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="cat-col-body">
                        {loadingIndustries ? (
                            <>
                                <div className="cat-skeleton" />
                                <div className="cat-skeleton" />
                                <div className="cat-skeleton" />
                            </>
                        ) : filteredIndustries.length > 0 ? (
                            filteredIndustries.map(ind => (
                                <div
                                    key={ind.id}
                                    className={`cat-item-row ${selectedIndustry?.id === ind.id ? 'selected' : ''}`}
                                    onClick={() => handleSelectIndustry(ind)}
                                >
                                    <div className="cat-item-info">
                                        <span className="cat-item-name" title={ind.name}>{ind.name}</span>
                                        <span className={`cat-status-badge ${ind.status?.toLowerCase() === 'active' ? 'active' : 'inactive'}`}>
                                            {ind.status || 'ACTIVE'}
                                        </span>
                                    </div>
                                    <div className="cat-item-actions">
                                        <button className="cat-action-btn" title="Edit" onClick={(e) => { e.stopPropagation(); openEditModal('INDUSTRY', ind, null); }}>
                                            <Edit2 size={14} />
                                        </button>
                                        <button className="cat-action-btn delete" title="Delete" onClick={(e) => openDeleteConfirm(e, 'INDUSTRY', ind)}>
                                            <Trash2 size={14} />
                                        </button>
                                        <ChevronRight size={16} style={{ color: '#94a3b8', marginLeft: '2px' }} />
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="cat-empty-state">
                                <p>{searchIndustry ? 'No matching industries.' : 'No industries created yet.'}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* COLUMN 2: EXPERTISE CATEGORIES */}
                <div className="cat-col-card">
                    <div className="cat-col-header">
                        <div className="cat-col-title-row">
                            <span className="cat-col-title">
                                Sub Categories
                                {selectedIndustry && <span className="cat-col-badge">{categories.length}</span>}
                            </span>
                            <button
                                className="cat-add-btn"
                                disabled={!selectedIndustry}
                                onClick={() => openAddModal('CATEGORY', selectedIndustry)}
                            >
                                <Plus size={14} /> Add Sub Category
                            </button>
                        </div>
                        <div className="cat-col-search">
                            <Search size={14} className="cat-search-icon" />
                            <input
                                type="text"
                                placeholder={selectedIndustry ? `Search in ${selectedIndustry.name}...` : 'Select category first'}
                                disabled={!selectedIndustry}
                                value={searchCategory}
                                onChange={e => setSearchCategory(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="cat-col-body">
                        {!selectedIndustry ? (
                            <div className="cat-empty-state">
                                <p>👈 Click on a Category row on the left to select it and unlock "+ Add Sub Category".</p>
                            </div>
                        ) : loadingCategories ? (
                            <>
                                <div className="cat-skeleton" />
                                <div className="cat-skeleton" />
                            </>
                        ) : filteredCategories.length > 0 ? (
                            filteredCategories.map(cat => (
                                <div
                                    key={cat.id}
                                    className={`cat-item-row ${selectedCategory?.id === cat.id ? 'selected' : ''}`}
                                    onClick={() => handleSelectCategory(cat)}
                                >
                                    <div className="cat-item-info">
                                        <span className="cat-item-name" title={cat.name}>{cat.name}</span>
                                        <span className={`cat-status-badge ${cat.status?.toLowerCase() === 'active' ? 'active' : 'inactive'}`}>
                                            {cat.status || 'ACTIVE'}
                                        </span>
                                    </div>
                                    <div className="cat-item-actions">
                                        <button className="cat-action-btn" title="Edit" onClick={(e) => { e.stopPropagation(); openEditModal('CATEGORY', cat, selectedIndustry); }}>
                                            <Edit2 size={14} />
                                        </button>
                                        <button className="cat-action-btn delete" title="Delete" onClick={(e) => openDeleteConfirm(e, 'CATEGORY', cat)}>
                                            <Trash2 size={14} />
                                        </button>
                                        <ChevronRight size={16} style={{ color: '#94a3b8', marginLeft: '2px' }} />
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="cat-empty-state">
                                <p>{searchCategory ? 'No matching categories.' : `No categories found inside '${selectedIndustry.name}'.`}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* COLUMN 3: SUB CATEGORIES */}
                <div className="cat-col-card">
                    <div className="cat-col-header">
                        <div className="cat-col-title-row">
                            <span className="cat-col-title">
                                Core Expertise
                                {selectedCategory && <span className="cat-col-badge">{subcategories.length}</span>}
                            </span>
                            <button
                                className="cat-add-btn"
                                disabled={!selectedCategory}
                                onClick={() => openAddModal('SUBCATEGORY', selectedCategory)}
                            >
                                <Plus size={14} /> Add Core Expertise
                            </button>
                        </div>
                        <div className="cat-col-search">
                            <Search size={14} className="cat-search-icon" />
                            <input
                                type="text"
                                placeholder={selectedCategory ? `Search in ${selectedCategory.name}...` : 'Select sub category first'}
                                disabled={!selectedCategory}
                                value={searchSubcategory}
                                onChange={e => setSearchSubcategory(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="cat-col-body">
                        {!selectedCategory ? (
                            <div className="cat-empty-state">
                                <p>👈 Click on a Sub Category row on the left to select it and unlock "+ Add Core Expertise".</p>
                            </div>
                        ) : loadingSubcategories ? (
                            <>
                                <div className="cat-skeleton" />
                                <div className="cat-skeleton" />
                            </>
                        ) : filteredSubcategories.length > 0 ? (
                            filteredSubcategories.map(sub => (
                                <div
                                    key={sub.id}
                                    className={`cat-item-row ${selectedSubcategory?.id === sub.id ? 'selected' : ''}`}
                                    onClick={() => handleSelectSubcategory(sub)}
                                >
                                    <div className="cat-item-info">
                                        <span className="cat-item-name" title={sub.name}>{sub.name}</span>
                                        <span className={`cat-status-badge ${sub.status?.toLowerCase() === 'active' ? 'active' : 'inactive'}`}>
                                            {sub.status || 'ACTIVE'}
                                        </span>
                                    </div>
                                    <div className="cat-item-actions">
                                        <button className="cat-action-btn" title="Edit" onClick={(e) => { e.stopPropagation(); openEditModal('SUBCATEGORY', sub, selectedCategory); }}>
                                            <Edit2 size={14} />
                                        </button>
                                        <button className="cat-action-btn delete" title="Delete" onClick={(e) => openDeleteConfirm(e, 'SUBCATEGORY', sub)}>
                                            <Trash2 size={14} />
                                        </button>
                                        <ChevronRight size={16} style={{ color: '#94a3b8', marginLeft: '2px' }} />
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="cat-empty-state">
                                <p>{searchSubcategory ? 'No matching subcategories.' : `No subcategories inside '${selectedCategory.name}'.`}</p>
                            </div>
                        )}
                    </div>
                </div>

            </div>

            {/* ADD / EDIT MODAL */}
            {modalState.isOpen && (
                <div className="cat-modal-backdrop" onClick={closeModal}>
                    <div className="cat-modal-box" onClick={e => e.stopPropagation()}>
                        <div className="cat-modal-header">
                            <h3>{modalState.mode === 'ADD' ? 'Add' : 'Edit'} {getLevelTitle(modalState.level)}</h3>
                            <button className="cat-modal-close" onClick={closeModal}><X size={18} /></button>
                        </div>
                        <form onSubmit={handleSubmitModal}>
                            <div className="cat-modal-body">
                                {modalState.parent && (
                                    <div className="cat-form-group">
                                        <label>Parent Context</label>
                                        <span className="cat-parent-badge">{modalState.parent.name}</span>
                                    </div>
                                )}
                                <div className="cat-form-group">
                                    <label>Name</label>
                                    <input
                                        type="text"
                                        placeholder={`Enter ${getLevelTitle(modalState.level)} name...`}
                                        value={formData.name}
                                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                                        required
                                        autoFocus
                                    />
                                </div>
                                <div className="cat-form-group">
                                    <label>Status</label>
                                    <select
                                        value={formData.status}
                                        onChange={e => setFormData({ ...formData, status: e.target.value })}
                                    >
                                        <option value="ACTIVE">ACTIVE</option>
                                        <option value="INACTIVE">INACTIVE</option>
                                    </select>
                                </div>
                            </div>
                            <div className="cat-modal-footer">
                                <button type="button" className="cat-btn-cancel" onClick={closeModal}>Cancel</button>
                                <button type="submit" className="cat-btn-submit" disabled={formSubmitting}>
                                    {formSubmitting ? 'Saving...' : modalState.mode === 'ADD' ? 'Create' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* DELETE CONFIRMATION MODAL */}
            {deleteModal.isOpen && (
                <div className="cat-modal-backdrop" onClick={closeDeleteConfirm}>
                    <div className="cat-modal-box" onClick={e => e.stopPropagation()}>
                        <div className="cat-modal-header">
                            <h3>Confirm Deletion</h3>
                            <button className="cat-modal-close" onClick={closeDeleteConfirm}><X size={18} /></button>
                        </div>
                        <div className="cat-modal-body">
                            <p style={{ margin: 0, fontSize: '0.92rem', color: '#334155', lineHeight: 1.5 }}>
                                Are you sure you want to delete the {getLevelTitle(deleteModal.level)} <strong>"{deleteModal.item?.name}"</strong>?
                            </p>
                            {deleteModal.error && (
                                <div style={{
                                    padding: '12px',
                                    borderRadius: '6px',
                                    backgroundColor: '#fef2f2',
                                    border: '1px solid #f87171',
                                    color: '#b91c1c',
                                    fontSize: '0.85rem',
                                    display: 'flex',
                                    gap: '8px',
                                    alignItems: 'flex-start'
                                }}>
                                    <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: '1px' }} />
                                    <span>{deleteModal.error}</span>
                                </div>
                            )}
                        </div>
                        <div className="cat-modal-footer">
                            <button type="button" className="cat-btn-cancel" onClick={closeDeleteConfirm}>Cancel</button>
                            <button
                                type="button"
                                className="cat-btn-delete"
                                disabled={deleteModal.loading}
                                onClick={handleConfirmDelete}
                            >
                                {deleteModal.loading ? 'Deleting...' : 'Confirm Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CategoryManagement;
